import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { StatusBadge, SourceTag, AiBlock, LimitationWarning } from "@/components/Badges";
import { WORKFLOW_STATUSES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowLeft, Sparkles, FileText, Upload, Trash2, GitCompare, Search, PenLine, Activity,
  ListChecks, AlertTriangle, CheckCircle2, Download, Flag, Loader2, Plus, Scale, ClipboardList,
  MessageCircle, Send, FileDown, User, Building2,
} from "lucide-react";

const DOC_TYPES = ["Receipt or invoice", "Order confirmation", "Contract or terms", "Cancellation policy",
  "Photos or video", "Complaint letter", "Business response", "Correspondence", "Bank or card statement", "Other"];

const Spinner = () => <Loader2 className="h-4 w-4 animate-spin" />;

const Section = ({ title, icon: Icon, action, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">{Icon && <Icon className="h-4 w-4 text-slate-400" />} {title}</h3>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Empty = ({ children }) => <p className="text-sm text-slate-400">{children}</p>;

const ReportButton = ({ matterId, target }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const send = async () => {
    await api.post(`/matters/${matterId}/report`, { target, reason: reason || "Unhelpful", comment: reason });
    toast.success("Thank you — this output has been flagged for review");
    setOpen(false); setReason("");
  };
  return open ? (
    <span className="flex items-center gap-2">
      <Input data-testid={`report-input-${target}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What was wrong?" className="h-8 w-48 text-xs" />
      <Button size="sm" variant="outline" className="h-8" onClick={send}>Send</Button>
    </span>
  ) : (
    <button data-testid={`report-btn-${target}`} onClick={() => setOpen(true)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-600">
      <Flag className="h-3 w-3" /> Report
    </button>
  );
};

export default function Matter() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get(`/matters/${id}`).then(({ data }) => setM(data)), [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(""); }
  };

  if (!m) return <div className="min-h-screen bg-slate-50"><Nav /><div className="p-16 text-center text-slate-400">Loading…</div></div>;

  const answers = m.intake_answers || {};
  const qmap = Object.fromEntries((m.questions || []).map((q) => [q.id, q]));

  const setStatus = async (status) => { await api.patch(`/matters/${id}`, { status }); load(); toast.success("Status updated"); };

  const exportMatter = () => {
    const lines = [`AI Consumer Champion — Dispute Summary`, `Title: ${m.title}`, `Problem: ${m.problem_label}`, `Status: ${WORKFLOW_STATUSES[m.status]?.label}`, ``, `== CLIENT FACTS ==`];
    Object.entries(answers).forEach(([k, v]) => lines.push(`${qmap[k]?.prompt || k}: ${Array.isArray(v) ? v.join(", ") : v}`));
    if (m.chronology) { lines.push(``, `== CASE SUMMARY ==`, m.chronology.summary || ""); lines.push(``, `== CHRONOLOGY ==`); (m.chronology.timeline || []).forEach((t) => lines.push(`${t.date || "?"} — ${t.event}`)); }
    if (m.issues) { lines.push(``, `== POTENTIAL ISSUES ==`); (m.issues.issues || []).forEach((i) => lines.push(`- ${i.issue}: ${i.next_step}`)); }
    if (m.claim_form) { lines.push(``, `== DRAFT consumer dispute WORDING ==`); (m.claim_form.sections || []).forEach((s) => lines.push(`[${s.label}]\n${s.content}\n`)); }
    const approved = (m.drafts || []).filter((d) => d.status === "approved_for_use");
    if (approved.length) { lines.push(``, `== APPROVED DRAFTS ==`); approved.forEach((d) => lines.push(`--- ${d.title} ---\n${d.content}\n`)); }
    lines.push(``, `Generated ${new Date().toLocaleString()}. AI-assisted; verify all facts, dates and authorities.`);
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${m.title.replace(/\s+/g, "_")}.txt`; a.click();
    toast.success("Matter summary exported");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Nav />
      <main className="mx-auto max-w-7xl px-6 py-8">
        <button data-testid="back-btn" onClick={() => nav("/app")} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> All matters</button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">{m.problem_label}</div>
            <h1 className="font-serif text-4xl font-semibold tracking-tight text-slate-900">{m.title}</h1>
            <div className="mt-2 flex items-center gap-3">
              <span data-testid="party-badge" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${m.party === "respondent" ? "border-slate-300 bg-slate-800 text-white" : "border-green-200 bg-green-50 text-green-900"}`}>
                {m.party === "respondent" ? "Business" : "Consumer"}
              </span>
              <StatusBadge status={m.status} />
              {!m.intake_complete && <button data-testid="resume-intake" onClick={() => nav(`/intake/${id}`)} className="text-sm text-amber-600 underline">Resume intake</button>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={m.status} onValueChange={setStatus}>
              <SelectTrigger data-testid="status-select" className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(WORKFLOW_STATUSES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`status-opt-${k}`}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="export-pdf-btn" className="rounded-full bg-green-800 hover:bg-green-900" onClick={() => window.open(`${API}/matters/${id}/export.pdf`, "_blank")}><FileDown className="mr-1.5 h-4 w-4" /> Export PDF</Button>
            <Button data-testid="export-btn" variant="outline" className="rounded-full" onClick={exportMatter}><Download className="mr-1.5 h-4 w-4" /> .txt</Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="mt-8">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
            {[["overview", "Overview", ClipboardList], ["chronology", "Chronology", ListChecks], ["issues", "Issues", AlertTriangle],
              ["documents", "Documents", FileText], ["compare", "Compare", GitCompare], ["claim", "Dispute draft", Scale],
              ["research", "Research", Search], ["drafts", "Drafts", PenLine], ["assistant", "Assistant", MessageCircle], ["activity", "Activity", Activity]].map(([v, l, Ic]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="gap-1.5 data-[state=active]:bg-white"><Ic className="h-3.5 w-3.5" /> {l}</TabsTrigger>
            ))}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-6 grid gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              {answers.deadline_concern?.includes("urgent") && (
                <LimitationWarning>
                  The consumer indicated an <strong>approaching deadline</strong>. Return windows, cancellation rights,
                  payment disputes and court limits can differ. Verify the applicable date independently.
                </LimitationWarning>
              )}
              <FactsSections answers={answers} qmap={qmap} />
              <FeedbackCard matterId={id} />
            </div>
            <div className="space-y-4">
              <Section title="Quick actions" icon={Sparkles}>
                <div className="grid gap-2">
                  <Button data-testid="qa-chronology" variant="outline" className="justify-start" disabled={busy} onClick={() => run("c", () => api.post(`/matters/${id}/chronology`), "Chronology generated")}>{busy === "c" ? <Spinner /> : <ListChecks className="h-4 w-4" />} Generate chronology</Button>
                  <Button data-testid="qa-issues" variant="outline" className="justify-start" disabled={busy} onClick={() => run("i", () => api.post(`/matters/${id}/issues`), "Issues identified")}>{busy === "i" ? <Spinner /> : <AlertTriangle className="h-4 w-4" />} Identify issues</Button>
                  <Button data-testid="qa-claim" variant="outline" className="justify-start" disabled={busy} onClick={() => run("f", () => api.post(`/matters/${id}/claim-form`), "Draft form prepared")}>{busy === "f" ? <Spinner /> : <Scale className="h-4 w-4" />} Draft consumer dispute wording</Button>
                </div>
              </Section>
              {m.chronology?.key_dates?.length > 0 && (
                <Section title="Key dates" icon={ListChecks}>
                  <div className="space-y-2">
                    {m.chronology.key_dates.map((k, i) => (
                      <div key={i} className={`rounded-md border px-3 py-2 text-sm ${k.is_limitation ? "border-red-200 bg-red-50" : "border-slate-100 bg-slate-50"}`}>
                        <div className="flex justify-between"><span className="font-medium text-slate-800">{k.label}</span><span className="font-mono text-xs text-slate-600">{k.date || "?"}</span></div>
                        {k.note && <p className="mt-0.5 text-xs text-slate-500">{k.note}</p>}
                      </div>
                    ))}
                  </div>
                </Section>
              )}
              {m.issues?.missing_information?.length > 0 && (
                <Section title="Missing information" icon={AlertTriangle}>
                  <ul className="space-y-1.5 text-sm text-slate-600">{m.issues.missing_information.map((x, i) => <li key={i} className="flex gap-2"><span className="text-amber-500">•</span> {x}</li>)}</ul>
                </Section>
              )}
            </div>
          </TabsContent>

          {/* CHRONOLOGY */}
          <TabsContent value="chronology" className="mt-6">
            <Section title="Chronology & case summary" icon={ListChecks}
              action={<div className="flex items-center gap-3">{m.chronology && <ReportButton matterId={id} target="chronology" />}<Button data-testid="gen-chronology" size="sm" disabled={busy} onClick={() => run("c", () => api.post(`/matters/${id}/chronology`), "Chronology generated")} className="bg-green-800 hover:bg-green-900">{busy === "c" ? <Spinner /> : <Sparkles className="h-4 w-4" />} {m.chronology ? "Regenerate" : "Generate"}</Button></div>}>
              {!m.chronology ? <Empty>Generate a chronology from the client's facts.</Empty> : (
                <AiBlock label="AI-generated chronology — verify against source documents">
                  <p className="text-sm leading-relaxed text-slate-700">{m.chronology.summary}</p>
                  <div className="mt-4 space-y-0">
                    {(m.chronology.timeline || []).map((t, i) => (
                      <div key={i} className="flex gap-4 border-l-2 border-slate-200 py-2 pl-4">
                        <span className="w-28 shrink-0 font-mono text-xs text-slate-500">{t.date ? `${t.approximate ? "~" : ""}${t.date}` : "undated"}</span>
                        <span className="text-sm text-slate-700">{t.event} <span className="text-xs text-slate-400">({t.source})</span></span>
                      </div>
                    ))}
                  </div>
                </AiBlock>
              )}
            </Section>
          </TabsContent>

          {/* ISSUES */}
          <TabsContent value="issues" className="mt-6">
            <Section title="Potential rights and remedies" icon={AlertTriangle}
              action={<div className="flex items-center gap-3">{m.issues && <ReportButton matterId={id} target="issues" />}<Button data-testid="gen-issues" size="sm" disabled={busy} onClick={() => run("i", () => api.post(`/matters/${id}/issues`), "Issues identified")} className="bg-green-800 hover:bg-green-900">{busy === "i" ? <Spinner /> : <Sparkles className="h-4 w-4" />} {m.issues ? "Regenerate" : "Identify"}</Button></div>}>
              {!m.issues ? <Empty>Identify potential issues (shown for consideration, not as guaranteed outcomes).</Empty> : (
                <Accordion type="multiple" className="space-y-2">
                  {(m.issues.issues || []).map((iss, i) => (
                    <AccordionItem key={i} value={`i${i}`} data-testid={`issue-${i}`} className="rounded-lg border border-slate-200 px-4">
                      <AccordionTrigger className="text-left text-sm font-medium text-slate-800 hover:no-underline">{iss.issue}</AccordionTrigger>
                      <AccordionContent className="space-y-3 text-sm">
                        <Field label="Facts that may support this" items={iss.supporting_facts} color="text-green-600" />
                        <Field label="Missing information" items={iss.missing_info} color="text-amber-500" />
                        <Field label="Possible weaknesses / alternative explanations" items={iss.weaknesses} color="text-red-500" />
                        {iss.legislation?.length > 0 && (
                          <div><p className="text-xs font-medium uppercase tracking-wider text-slate-400">Relevant legislation</p>
                            <div className="mt-1 flex flex-wrap gap-2">{iss.legislation.map((l, j) => <span key={j} className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">{l.name} {l.reference} {l.verify && <span className="text-amber-600">· verify</span>}</span>)}</div></div>
                        )}
                        <div className="rounded-md bg-slate-50 p-3"><p className="text-xs font-medium text-slate-500">Next recommended step</p><p className="text-slate-700">{iss.next_step}</p>{iss.signpost && <p className="mt-1 text-xs text-blue-700">Signpost: {iss.signpost}</p>}</div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </Section>
          </TabsContent>

          <TabsContent value="documents" className="mt-6"><Documents matterId={id} docs={m.documents || []} reload={load} /></TabsContent>
          <TabsContent value="compare" className="mt-6"><Compare matterId={id} docs={m.documents || []} comparisons={m.comparisons || []} reload={load} /></TabsContent>

          {/* CLAIM FORM */}
          <TabsContent value="claim" className="mt-6"><ClaimForm m={m} busy={busy} run={run} matterId={id} reload={load} /></TabsContent>

          <TabsContent value="research" className="mt-6"><Research matterId={id} research={m.research || []} reload={load} /></TabsContent>
          <TabsContent value="drafts" className="mt-6"><Drafts matterId={id} drafts={m.drafts || []} reload={load} /></TabsContent>
          <TabsContent value="assistant" className="mt-6"><Assistant matterId={id} /></TabsContent>

          {/* ACTIVITY */}
          <TabsContent value="activity" className="mt-6">
            <Section title="Activity & audit history" icon={Activity}>
              <div className="space-y-1.5">
                {(m.activity || []).map((a) => (
                  <div key={a.id} className="flex items-center justify-between border-b border-slate-50 py-1.5 text-sm">
                    <span className="text-slate-700">{a.event_type.replace(/_/g, " ")}</span>
                    <span className="font-mono text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                ))}
                {(m.activity || []).length === 0 && <Empty>No activity yet.</Empty>}
              </div>
            </Section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const Field = ({ label, items, color }) => (items?.length ? (
  <div><p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
    <ul className="mt-1 space-y-1">{items.map((x, i) => <li key={i} className="flex gap-2 text-slate-700"><span className={color}>•</span> {x}</li>)}</ul></div>
) : null);

function FactsSections({ answers, qmap }) {
  const groups = { consumer: [], business: [], case: [] };
  Object.entries(answers).forEach(([k, v]) => {
    const sec = qmap[k]?.section || "case";
    groups[sec] ? groups[sec].push([k, v]) : groups.case.push([k, v]);
  });
  const card = (title, icon, entries, accent) => (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3.5">
        <span className={`flex h-7 w-7 items-center justify-center rounded-md ${accent}`}>{icon}</span>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="p-5">
        {entries.length === 0 ? <Empty>No details captured yet.</Empty> : (
          <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
            {entries.map(([k, v]) => (
              <div key={k} className="border-b border-slate-50 py-1.5">
                <dt className="text-xs text-slate-400">{qmap[k]?.prompt || k}</dt>
                <dd className="text-sm font-medium text-slate-800">{Array.isArray(v) ? v.join(", ") : (v || "—")}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
  if (Object.keys(answers).length === 0) return <Section title="Client facts" icon={ClipboardList}><Empty>No intake answers yet.</Empty></Section>;
  return (
    <div className="space-y-4" data-testid="facts-sections">
      {card("Consumer", <User className="h-4 w-4 text-white" />, groups.consumer, "bg-green-800")}
      {card("Business", <Building2 className="h-4 w-4 text-white" />, groups.business, "bg-slate-700")}
      {groups.case.length > 0 && card("The claim & events", <ClipboardList className="h-4 w-4 text-white" />, groups.case, "bg-blue-700")}
    </div>
  );
}

function FeedbackCard({ matterId }) {
  const [done, setDone] = useState(false);
  const [comment, setComment] = useState("");
  const [helpful, setHelpful] = useState(null);
  const submit = async (h) => {
    setHelpful(h);
    await api.post(`/matters/${matterId}/feedback`, { helpful: h, comment });
    setDone(true); toast.success("Thanks for your feedback");
  };
  if (done) return <Section title="Feedback" icon={CheckCircle2}><p className="text-sm text-slate-500">Thank you — your feedback helps improve the product.</p></Section>;
  return (
    <Section title="Feedback" icon={CheckCircle2}>
      <p className="text-sm font-medium text-slate-700">Did this help you prepare the matter more efficiently?</p>
      <Textarea data-testid="feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional: what worked, what didn't, time saved…" rows={2} className="mt-3" />
      <div className="mt-3 flex gap-2">
        <Button data-testid="feedback-yes" size="sm" className="bg-green-800 hover:bg-green-900" onClick={() => submit(true)}>Yes, it helped</Button>
        <Button data-testid="feedback-no" size="sm" variant="outline" onClick={() => submit(false)}>Not really</Button>
      </div>
    </Section>
  );
}

function Documents({ matterId, docs, reload }) {
  const [mode, setMode] = useState("upload");
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!title) return toast.error("Add a title");
    setBusy(true);
    try {
      if (mode === "upload") {
        if (!file) { setBusy(false); return toast.error("Choose a file"); }
        const fd = new FormData(); fd.append("file", file); fd.append("doc_type", docType); fd.append("title", title);
        await api.post(`/matters/${matterId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        if (!text) { setBusy(false); return toast.error("Paste document text"); }
        await api.post(`/matters/${matterId}/documents/text`, { doc_type: docType, title, text_content: text });
      }
      toast.success("Document added"); setTitle(""); setText(""); setFile(null); reload();
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  const del = async (docId) => { await api.delete(`/documents/${docId}`); toast.success("Removed"); reload(); };

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <Section title="Uploaded documents" icon={FileText}>
          <div className="space-y-2">
            {docs.length === 0 && <Empty>No documents yet. Add contracts, letters, policies or settlements.</Empty>}
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-500"><FileText className="h-4 w-4" /></span>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{d.title}</p>
                    <p className="text-xs text-slate-400">{d.doc_type} · {d.extracted_text ? `${d.extracted_text.length} chars of text` : "no text extracted"}</p>
                  </div>
                </div>
                <button data-testid={`del-doc-${d.id}`} onClick={() => del(d.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </Section>
      </div>
      <Section title="Add a document" icon={Plus}>
        <div className="mb-3 flex gap-1 rounded-lg bg-slate-100 p-1 text-sm">
          <button data-testid="doc-mode-upload" onClick={() => setMode("upload")} className={`flex-1 rounded py-1.5 ${mode === "upload" ? "bg-white font-medium" : "text-slate-500"}`}>Upload file</button>
          <button data-testid="doc-mode-text" onClick={() => setMode("text")} className={`flex-1 rounded py-1.5 ${mode === "text" ? "bg-white font-medium" : "text-slate-500"}`}>Paste text</button>
        </div>
        <div className="space-y-3">
          <Select value={docType} onValueChange={setDocType}><SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger><SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          <Input data-testid="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title" />
          {mode === "upload" ? (
            <Input data-testid="doc-file" type="file" accept=".pdf,.txt,.png,.jpg,.jpeg,.docx" onChange={(e) => setFile(e.target.files[0])} />
          ) : (
            <Textarea data-testid="doc-text" value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder="Paste the document text here for comparison and review…" />
          )}
          <p className="text-xs text-slate-400">PDF and text are extracted for AI comparison. For scanned images, paste the text.</p>
          <Button data-testid="add-doc-btn" disabled={busy} onClick={add} className="w-full bg-green-800 hover:bg-green-900">{busy ? <Spinner /> : <Upload className="h-4 w-4" />} Add document</Button>
        </div>
      </Section>
    </div>
  );
}

function Compare({ matterId, docs, comparisons, reload }) {
  const [a, setA] = useState(""); const [b, setB] = useState(""); const [busy, setBusy] = useState(false);
  const [q, setQ] = useState(""); const [ans, setAns] = useState(null); const [qbusy, setQbusy] = useState(false);

  const compare = async () => {
    if (!a || !b || a === b) return toast.error("Choose two different documents");
    setBusy(true);
    try { await api.post(`/matters/${matterId}/compare`, { doc_id_a: a, doc_id_b: b }); toast.success("Comparison ready"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Comparison failed"); } finally { setBusy(false); }
  };
  const askCompat = async () => {
    if (!a || !b || !q) return toast.error("Pick two documents and ask a question");
    setQbusy(true);
    try { const { data } = await api.post(`/matters/${matterId}/compatibility`, { doc_id_a: a, doc_id_b: b, question: q }); setAns(data.answer); }
    catch { toast.error("Failed"); } finally { setQbusy(false); }
  };
  const cat = (label, items, cls) => items?.length ? (
    <div><p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p><ul className="mt-1 space-y-1 text-sm">{items.map((x, i) => <li key={i} className={`rounded px-2 py-1 ${cls}`}>{x}</li>)}</ul></div>
  ) : null;

  return (
    <div className="space-y-4">
      <Section title="Compare two documents" icon={GitCompare}>
        {docs.length < 2 ? <Empty>Add at least two documents to compare.</Empty> : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select value={a} onValueChange={setA}><SelectTrigger data-testid="compare-a"><SelectValue placeholder="Document A" /></SelectTrigger><SelectContent>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent></Select>
              <Select value={b} onValueChange={setB}><SelectTrigger data-testid="compare-b"><SelectValue placeholder="Document B" /></SelectTrigger><SelectContent>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="mt-3 flex gap-2">
              <Button data-testid="run-compare" disabled={busy} onClick={compare} className="bg-green-800 hover:bg-green-900">{busy ? <Spinner /> : <GitCompare className="h-4 w-4" />} Substantive comparison</Button>
            </div>
            <div className="mt-4 flex gap-2">
              <Input data-testid="compat-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask: are these provisions compatible?" />
              <Button data-testid="run-compat" variant="outline" disabled={qbusy} onClick={askCompat}>{qbusy ? <Spinner /> : "Ask"}</Button>
            </div>
            {ans && <AiBlock className="mt-3" label="AI answer — verify"><p className="whitespace-pre-wrap text-sm text-slate-700">{ans}</p></AiBlock>}
          </>
        )}
      </Section>

      {comparisons.map((c) => (
        <Section key={c.id} title={`${c.doc_a.title} ↔ ${c.doc_b.title}`} icon={GitCompare} action={<ReportButton matterId={matterId} target="comparison" />}>
          <AiBlock label="Substantive difference report — verify against source documents">
            <p className="text-sm text-slate-700">{c.report.summary}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {cat("Changes to rights / obligations", c.report.rights_changes, "bg-blue-50 text-blue-900")}
              {cat("New clauses", c.report.new_clauses, "bg-green-50 text-green-900")}
              {cat("Removed clauses", c.report.removed_clauses, "bg-red-50 text-red-900")}
              {cat("Inconsistent dates / figures", c.report.inconsistencies, "bg-amber-50 text-amber-900")}
              {cat("Potential conflicts", c.report.conflicts, "bg-orange-50 text-orange-900")}
              {cat("May affect a claim", c.report.claim_impact, "bg-purple-50 text-purple-900")}
              {cat("Requires user review", c.report.review_flags, "bg-slate-100 text-slate-800")}
              {cat("Questions for client / employer", c.report.client_questions, "bg-slate-50 text-slate-700")}
            </div>
          </AiBlock>
        </Section>
      ))}
    </div>
  );
}

function ClaimForm({ m, busy, run, matterId, reload }) {
  const [edits, setEdits] = useState({});
  const cf = m.claim_form;
  const isResp = m.party === "respondent";
  const formRef = cf?.form_ref || (isResp ? "business response" : "consumer dispute");
  const formUrl = "https://www.gov.uk/guidance/help-for-consumers";
  const save = async (idx, content) => {
    const sections = cf.sections.map((s, i) => i === idx ? { ...s, content, needs_clarification: false } : s);
    const updated = { ...cf, sections };
    await api.patch(`/matters/${matterId}`, { claim_form: updated });
    await api.post(`/matters/${matterId}/intake-event`, { event_type: "claim_form_edited", metadata: { section: cf.sections[idx].key } }).catch(() => {});
    setEdits({ ...edits, [idx]: undefined });
    toast.success("Section saved");
    reload();
  };
  return (
    <div className="space-y-4">
      <Section title={`Draft ${formRef} wording`} icon={Scale}
        action={<div className="flex items-center gap-2">
          {cf && <Button data-testid="download-claim-pdf" size="sm" variant="outline" className="rounded-full" onClick={() => window.open(`${API}/matters/${matterId}/claim-form.pdf`, "_blank")}><FileDown className="h-4 w-4" /> Download {formRef} (PDF)</Button>}
          <Button data-testid="gen-claim" size="sm" disabled={busy} onClick={() => run("f", () => api.post(`/matters/${matterId}/claim-form`), "Draft prepared")} className="bg-green-800 hover:bg-green-900">{busy === "f" ? <Spinner /> : <Sparkles className="h-4 w-4" />} {cf ? "Regenerate" : "Generate"}</Button>
        </div>}>
        <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"><AlertTriangle className="h-4 w-4" /> Never sent automatically. Review every section before using it, and check the relevant <a className="ml-1 underline" href={formUrl} target="_blank" rel="noreferrer">official consumer guidance</a>.</div>
        {!cf ? <Empty>Map the {isResp ? "business's" : "client's"} answers to {formRef} sections.</Empty> : (
          <div className="space-y-4">
            {cf.inconsistencies?.length > 0 && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900"><p className="font-medium">Facts that require clarification</p><ul className="mt-1 list-disc pl-5">{cf.inconsistencies.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
            {cf.missing?.length > 0 && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"><p className="font-medium">Unanswered / missing</p><ul className="mt-1 list-disc pl-5">{cf.missing.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
            {cf.sections.map((s, i) => (
              <div key={i} data-testid={`claim-section-${s.key}`} className="rounded-lg border border-slate-200">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
                  <span className="text-sm font-medium text-slate-800">{s.label} {s.needs_clarification && <span className="ml-1 text-xs text-amber-600">· needs clarification</span>}</span>
                  <SourceTag source="ai" />
                </div>
                <div className="p-4">
                  <Textarea data-testid={`claim-edit-${s.key}`} value={edits[i] !== undefined ? edits[i] : s.content} onChange={(e) => setEdits({ ...edits, [i]: e.target.value })} rows={3} className="text-sm" />
                  {s.note && <p className="mt-1 text-xs text-slate-400">{s.note}</p>}
                  {edits[i] !== undefined && edits[i] !== s.content && <Button size="sm" className="mt-2" onClick={() => save(i, edits[i])}>Save section</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Research({ matterId, research, reload }) {
  const [q, setQ] = useState(""); const [busy, setBusy] = useState(false);
  const suggestions = ["What refund remedy may apply to faulty goods?", "Can I cancel an online service contract?", "Could chargeback or Section 75 apply?", "What evidence would strengthen this dispute?"];
  const ask = async (question) => {
    const qq = question || q; if (!qq) return;
    setBusy(true);
    try { await api.post(`/matters/${matterId}/research`, { question: qq }); setQ(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Research failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Section title="Consumer research workspace" icon={Search}>
        <div className="flex gap-2">
          <Input data-testid="research-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Ask a consumer research question…" />
          <Button data-testid="research-ask" disabled={busy} onClick={() => ask()} className="bg-green-800 hover:bg-green-900">{busy ? <Spinner /> : <Search className="h-4 w-4" />} Research</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">{suggestions.map((s) => <button key={s} data-testid="research-suggestion" onClick={() => ask(s)} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-slate-400">{s}</button>)}</div>
      </Section>
      {research.map((r) => (
        <Section key={r.id} title={r.question} icon={Search} action={<ReportButton matterId={matterId} target="research" />}>
          <AiBlock label="AI research — authorities NOT independently verified">
            <p className="whitespace-pre-wrap text-sm text-slate-700">{r.result.answer}</p>
            {r.result.sources?.length > 0 && (
              <div className="mt-4 space-y-2">
                {r.result.sources.map((s, i) => (
                  <div key={i} className="rounded-md border border-slate-200 bg-white p-3 text-sm">
                    <div className="flex items-center justify-between"><span className="font-medium text-slate-800">{s.source_name}</span><span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">Unverified</span></div>
                    {s.citation && <p className="font-mono text-xs text-slate-500">{s.citation}</p>}
                    <p className="mt-1 text-slate-600">{s.relevance}</p>
                    {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline">{s.url}</a>}
                    <p className="mt-1 text-xs text-slate-400">Last checked {s.date_checked} · {s.warning}</p>
                  </div>
                ))}
              </div>
            )}
            {r.result.disclaimer && <p className="mt-3 text-xs italic text-slate-400">{r.result.disclaimer}</p>}
          </AiBlock>
        </Section>
      ))}
    </div>
  );
}

const DRAFT_OPTIONS = [
  ["advice_summary", "Consumer options summary"], ["grievance_letter", "Complaint and refund letter"], ["appeal_letter", "Final response and escalation letter"],
  ["information_request", "Request for information"], ["preliminary_claim_summary", "Dispute summary"],
  ["tribunal_form_wording", "Letter before action wording"], ["document_review_report", "Document review report"],
  ["client_questions", "Questions for the consumer"], ["counsel_instructions", "Ombudsman or ADR escalation summary"],
  ["lawyer_review_report", "Review report"],
];

function Drafts({ matterId, drafts, reload }) {
  const [kind, setKind] = useState(DRAFT_OPTIONS[0][0]); const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { await api.post(`/matters/${matterId}/drafts`, { kind }); toast.success("Draft created"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Section title="Drafting assistant" icon={PenLine}>
        <div className="flex flex-wrap gap-2">
          <Select value={kind} onValueChange={setKind}><SelectTrigger data-testid="draft-kind" className="w-72"><SelectValue /></SelectTrigger><SelectContent>{DRAFT_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <Button data-testid="gen-draft" disabled={busy} onClick={gen} className="bg-green-800 hover:bg-green-900">{busy ? <Spinner /> : <Sparkles className="h-4 w-4" />} Generate draft</Button>
        </div>
        <p className="mt-2 text-xs text-slate-400">Every draft is editable and based only on verified facts. Placeholders like [TO CONFIRM] flag missing information.</p>
      </Section>
      {drafts.map((d) => <DraftCard key={d.id} draft={d} reload={reload} />)}
      {drafts.length === 0 && <Empty>No drafts yet.</Empty>}
    </div>
  );
}

function DraftCard({ draft, reload }) {
  const [content, setContent] = useState(draft.content);
  const [dirty, setDirty] = useState(false);
  const save = async () => { await api.patch(`/drafts/${draft.id}`, { content }); setDirty(false); toast.success("Saved"); reload(); };
  const setStatus = async (status) => { await api.patch(`/drafts/${draft.id}`, { status }); toast.success("Status updated"); reload(); };
  const del = async () => { await api.delete(`/drafts/${draft.id}`); toast.success("Deleted"); reload(); };
  return (
    <Section title={draft.title} icon={PenLine}
      action={<div className="flex items-center gap-2"><SourceTag source={draft.source} /><StatusBadge status={draft.status} /></div>}>
      <Textarea data-testid={`draft-content-${draft.id}`} value={content} onChange={(e) => { setContent(e.target.value); setDirty(true); }} rows={8} className="font-sans text-sm" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {dirty && <Button data-testid={`save-draft-${draft.id}`} size="sm" onClick={save}>Save edits</Button>}
        <Button data-testid={`download-draft-${draft.id}`} size="sm" variant="outline" className="rounded-full" onClick={() => window.open(`${API}/drafts/${draft.id}.pdf`, "_blank")}><FileDown className="mr-1 h-4 w-4" /> Download PDF</Button>
        <Select value={draft.status} onValueChange={setStatus}><SelectTrigger data-testid={`draft-status-${draft.id}`} className="h-9 w-56"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(WORKFLOW_STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
        {draft.status !== "approved_for_use" && <Button data-testid={`approve-draft-${draft.id}`} size="sm" variant="outline" className="text-green-800" onClick={() => setStatus("approved_for_use")}><CheckCircle2 className="mr-1 h-4 w-4" /> Approve</Button>}
        <button data-testid={`del-draft-${draft.id}`} onClick={del} className="ml-auto text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
      </div>
    </Section>
  );
}

function Assistant({ matterId }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/matters/${matterId}/chat`).then(({ data }) => setMsgs(data)); }, [matterId]);
  const suggestions = ["Summarise this matter in three sentences.", "What are the strongest and weakest points?", "What should I ask the client next?", "Which deadlines should I check?"];
  const send = async (text) => {
    const t = text || input; if (!t || busy) return;
    setBusy(true); setInput("");
    setMsgs((p) => [...p, { id: `tmp-${Date.now()}`, role: "user", content: t }]);
    try {
      const { data } = await api.post(`/matters/${matterId}/chat`, { message: t });
      setMsgs((p) => [...p.filter((x) => !String(x.id).startsWith("tmp")), data.user, data.assistant]);
    } catch { toast.error("Assistant could not respond"); }
    finally { setBusy(false); }
  };
  return (
    <Section title="Matter assistant" icon={MessageCircle}>
      <p className="mb-3 text-xs text-slate-400">Ask questions about this matter. The assistant answers using only the facts, chronology and issues in this workspace, and does not provide certain outcomes.</p>
      <div data-testid="chat-messages" className="max-h-[460px] space-y-3 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50/50 p-4">
        {msgs.length === 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => <button key={s} data-testid="chat-suggestion" onClick={() => send(s)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:border-green-700">{s}</button>)}
          </div>
        )}
        {msgs.map((mm, i) => (
          <div key={mm.id || i} className={`flex ${mm.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${mm.role === "user" ? "bg-green-800 text-white" : "border border-slate-200 bg-white text-slate-700"}`}>
              {mm.content}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div></div>}
      </div>
      <div className="mt-3 flex gap-2">
        <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask the assistant…" />
        <Button data-testid="chat-send" disabled={busy} onClick={() => send()} className="bg-green-800 hover:bg-green-900"><Send className="h-4 w-4" /></Button>
      </div>
    </Section>
  );
}
